import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components/macro';

import { calculateConversion } from '../../data/gtm';
import { useTracking } from '../../data/gtm';
import { uiColors } from '../../common/styles';
import { useAuth, useComposition, useFrami, useImages, useNavigation, useCompositionTemplates } from '../../context';
import { IMAGE_DRAWER, TEMPLATE_DRAWER } from '../../providers/NavigationProvider';
import { Api, Toastify } from '../../utilities';
import { generateRandomFileName } from '../../utilities/helpers';
import { logError } from '../../utilities/logError';

const Wrapper = styled.div`
    width: 100%;
    height: 100%;
    background: ${(props) => (props.$isDragActive ? uiColors.PRIMARY_200 : '')};
`;

const Dropzone = ({ noClick, style, children, selectRandomComposition=false, frameId }) => {
    const { user, eventsCounter, setEventsCounter, updateGoogleEventsCounter } = useAuth();
    const { currentFrami, updateFramiWallShape } = useFrami();
    const { addImagesToFrames, currentComp } = useComposition();
    const { addImage, determineThumbnailSize, setUploadProgress, setImagesLoading } = useImages();
    const { activeDrawer, drawerStack, openDrawer, closeDrawer } = useNavigation();
    const { findRandomTemplateIndex, setCurrentTemplate, setFilterFrameCounts } = useCompositionTemplates();
    const { pushEvent } = useTracking();

    const onDrop = useCallback(
        async (acceptedFiles, rejectedFiles) => {

            setImagesLoading(true);

            if (activeDrawer !== IMAGE_DRAWER) openDrawer(IMAGE_DRAWER);
            if (selectRandomComposition) {
                if (activeDrawer !== TEMPLATE_DRAWER) openDrawer(TEMPLATE_DRAWER);

                let width = 1200;
                let height = 800;
                
                console.log('selectRandomComposition images:', acceptedFiles.length);
                const updateSize = (width, height) => {
                    const wallShape = [
                        [0, 0],
                        [width, 0],
                        [width, height],
                        [0, height],
                        [0, 0],
                    ];
                    updateFramiWallShape(wallShape);
                };
                if (acceptedFiles.length > 20) {
                    width = 2500;
                    height = 1500
                } else if (acceptedFiles.length > 10) {
                    width = 1500;
                    height = 1000;
                }
                updateSize(width, height);

                console.log('selectRandomComposition:', currentComp?.template_id, (currentComp?.template_id === 3700666 && acceptedFiles.length !== 4));
                if (currentComp?.template_id === 3700666 && acceptedFiles.length !== 4) {
                    console.log('selectRandomComposition findRandomTemplateIndex:', acceptedFiles.length, {width: width, height: height});
                    const templateIndex = await findRandomTemplateIndex(acceptedFiles.length, {width: width, height: height});
                    console.log('selectRandomComposition findRandomTemplateIndex result:', templateIndex);
                    setCurrentTemplate(templateIndex);
                }
                if (acceptedFiles.length > 2) {
                    const frameCountFilter = Array.from({ length: acceptedFiles.length }, (_, i) => i + 1)
                        .sort((a, b) => b - a)
                        .slice(0, 5);
                    setFilterFrameCounts(frameCountFilter);
                }

            }

            const uploadedImages = [];
            const updatedEventsCount = (eventsCounter['photo_upload'] || 0) + 1;
            setEventsCounter((prev) => ({ ...prev, photo_upload: updatedEventsCount }));
            updateGoogleEventsCounter({ ...eventsCounter, photo_upload: updatedEventsCount });

            pushEvent('photo_upload', {
                count: acceptedFiles.length,
                value:
                    (Math.round(
                        calculateConversion('photo_upload', updatedEventsCount) *
                            currentComp?.price?.rounded?.total *
                            100
                    ) / 100) || 0,
                currency: currentFrami.currency,
                through: 'upload',
            });

            for (let i = 0; i < acceptedFiles.length; i++) {
                const file = acceptedFiles[i];

                const filename = generateRandomFileName(file.name);

                const formData = new FormData();
                formData.append('file', file, file.name);
                formData.append('name', filename);
                formData.append('frami_id', currentFrami.id);
                formData.append('size', determineThumbnailSize());

                const config = {
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total / acceptedFiles.length
                        );
                        setUploadProgress((i * 100) / acceptedFiles.length + percentCompleted); // Update the progress
                    },
                };

                try {
                    const response = await Api.post('/composer/image/', formData, config);

                    // Add to array of uploaded images
                    uploadedImages.push(response);
                    addImagesToFrames([response], frameId);

                    // Add the image with the response data to the state
                    addImage(response);
                } catch (err) {
                    logError(err);
                    Toastify.error(err.response?.data ?? err.message);
                }
            }
            setUploadProgress(null);
            setImagesLoading(false);
        },
        [addImage, activeDrawer]
    );

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        accept: {
            'image/*': ['.png', '.jpg', '.heic', '.jpeg', '.gif', '.bmp', '.tiff', '.webp'],
        },
        noClick: noClick,
        noKeyboard: true,
        onDrop,
        onDragEnter: () => {
            if (activeDrawer !== IMAGE_DRAWER) openDrawer(IMAGE_DRAWER);
        },
        onDragLeave: () => {
            if (drawerStack.length > 1) closeDrawer();
        },
    });

    return (
        <Wrapper {...getRootProps()} $isDragActive={isDragActive} style={style}>
            <input data-cy="file-input" {...getInputProps()} />
            {typeof children === 'function' ? children(open) : children}
        </Wrapper>
    );
};

export default Dropzone;
